import {
  createContext,
  useContext,
  ReactNode,
  useReducer,
  useMemo,
  useEffect,
  useRef
} from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken } from 'src/Config/axiosApi';
import { AUTH_DISABLED } from 'src/App/Providers';
import {
  LIVE_STATUS_IDLE,
  PredictionDelta,
  PredictionOpportunity,
  WSPredictionResponse
} from '../types';
import { predictionsReducer, initialState } from './predictionsReducer';
import { useUpdates } from 'src/Modules/Arbitrage/context/updatesContext';
import { useLiveOpportunitiesSnapshot } from '../api/useLiveOpportunitiesSnapshot';

interface LivePredictionsContextProps {
  /** Live opportunities streamed from the parallel low-latency pipeline. */
  liveOpportunities: PredictionOpportunity[];
  liveCount: number;
  /** True when the live-WS deployment is configured for this environment.
   *  False ⇒ consumers should fall back to filtering live pairs out of the
   *  regular PredictionsProvider's cache. */
  enabled: boolean;
  /** True when enabled AND the live socket is currently connected. */
  isConnected: boolean;
}

const LivePredictionsContext = createContext<
  LivePredictionsContextProps | undefined
>(undefined);

export const LivePredictionsProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const accessToken = _getToken();
  const { isPredictionsRunning } = useUpdates();

  // Live WS is opt-in per environment. When the env var is missing we skip
  // the socket entirely — the existing PredictionsProvider already exposes
  // a fallback liveOpportunities (filtered from the regular cache) so the
  // Live tab still works, just without the sub-100ms latency benefit.
  const liveUrl = import.meta.env.VITE_LIVE_PREDICTIONS_WS_URL as
    | string
    | undefined;
  const enabled =
    Boolean(liveUrl) && isPredictionsRunning && (AUTH_DISABLED || !!accessToken);

  const [state, dispatch] = useReducer(predictionsReducer, initialState);

  const { socket } = useSocket(
    enabled && liveUrl ? liveUrl + '/predictions/live' : '',
    {
      enabled,
      auth: (cb: (data: { token: string }) => void) =>
        cb({ token: _getToken() })
    }
  );

  const { lastMessage: snapshotMessage } = useSocketEvent<WSPredictionResponse>(
    socket,
    'prediction_snapshot'
  );
  const { lastMessage: updateMessage } = useSocketEvent<WSPredictionResponse>(
    socket,
    'prediction_update'
  );
  const { lastMessage: deltaMessage } = useSocketEvent<PredictionDelta>(
    socket,
    'prediction_delta'
  );

  const hasSubscribed = useRef(false);

  const { data: snapshotData } = useLiveOpportunitiesSnapshot(enabled);

  useEffect(() => {
    if (!snapshotData) return;
    dispatch({
      type: 'SNAPSHOT',
      data: snapshotData.data,
      count: snapshotData.count,
      timestamp: snapshotData.timestamp ?? Date.now()
    });
  }, [snapshotData]);

  useEffect(() => {
    if (!enabled) {
      // Predictions off / env missing — clear stale opps so the Live tab
      // doesn't show last-known data when reconnecting later.
      dispatch({
        type: 'SNAPSHOT',
        data: [],
        count: 0,
        timestamp: Date.now()
      });
    }
  }, [enabled]);

  useEffect(() => {
    if (!socket.connected) {
      hasSubscribed.current = false;
      return;
    }
    if (!hasSubscribed.current) {
      socket.emit('subscribe_predictions');
      hasSubscribed.current = true;
    }
  }, [socket.connected]);

  useEffect(() => {
    if (snapshotMessage?.type === 'SNAPSHOT') dispatch(snapshotMessage);
  }, [snapshotMessage]);
  useEffect(() => {
    if (updateMessage?.type === 'UPDATE') dispatch(updateMessage);
  }, [updateMessage]);
  useEffect(() => {
    if (
      deltaMessage?.type === 'DELTA_UPDATE' ||
      deltaMessage?.type === 'DELTA_REMOVE'
    ) {
      dispatch(deltaMessage);
    }
  }, [deltaMessage]);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch({ type: 'CLEANUP' });
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  // The live calculator only ever caches live opps, but we belt-and-brace
  // here so a stray non-live entry (e.g. a payload that crossed the cutover
  // mid-flip) doesn't pollute the tab.
  const liveOpportunities = useMemo(
    () =>
      Array.from(state.cache.values()).filter(
        o => !o.archived && o.liveStatus !== LIVE_STATUS_IDLE
      ),
    [state.cache]
  );

  const value: LivePredictionsContextProps = {
    liveOpportunities,
    liveCount: liveOpportunities.length,
    enabled,
    isConnected: enabled && socket.connected
  };

  return (
    <LivePredictionsContext.Provider value={value}>
      {children}
    </LivePredictionsContext.Provider>
  );
};

export const useLivePredictions = () => {
  const context = useContext(LivePredictionsContext);
  if (!context) {
    throw new Error(
      'useLivePredictions must be used within a LivePredictionsProvider'
    );
  }
  return context;
};
